import { computed, makeObservable, observable, runInAction } from 'mobx';
import { ImagesPolylineInfo } from './ImagesPolylineInfo';
import type {
  SlicePositionInfo,
  SliceOrientationInfo,
  SliceVisualizationInfo,
  XRaySlicerParams
} from './ImageStackController';
import type { IImagesInfo, IImagesPolylineInfo } from './interfaces';
import { ImagesMode } from './interfaces';
import type * as THREE from 'three';

export class ImagesInfo implements IImagesInfo {
  @observable private _areImagesReady = false;

  @observable private _slicesCount = 0;

  @observable private _currentSlice = 0;

  @observable private _xRaySlicerParams?: XRaySlicerParams = undefined;

  @observable private _sliceDistance = 0;

  @observable private _contrastWindowLevel = 0;

  @observable private _contrastWindowWidth = 0;

  @observable private _reductionFactorFromOriginal = 0;

  @observable private _slicePosition = 0;

  @observable private _totalFramesCount = 0;

  @observable private _loadedFramesCount = 0;

  @observable private _areReducedImagesLoading = true;

  @observable private _imageReductionFactor = 0;

  @observable private _imageMode = ImagesMode.MPR;

  private _polylineInfo: IImagesPolylineInfo = new ImagesPolylineInfo();

  public constructor() {
    makeObservable(this);
  }

  public setFromSliceOrientation(info: SliceOrientationInfo): void {
    runInAction(() => {
      this._xRaySlicerParams = info.xRaySlicerParams;
      this._sliceDistance = info.voxelDimensions.z;
      this._slicesCount = info.slicesCount;
      this._imageReductionFactor = info.imageReductionFactor;
    });
  }

  public setFromSlicePosition(info: SlicePositionInfo): void {
    runInAction(() => {
      this._currentSlice = info.sliceIndex;
      this._slicePosition = info.slicePosition;
    });
  }

  public setFromSliceVisualization(info: SliceVisualizationInfo): void {
    runInAction(() => {
      this._contrastWindowLevel = info.contrastWindowLevel;
      this._contrastWindowWidth = info.contrastWindowWidth;
      this._reductionFactorFromOriginal = info.reductionFactorFromOriginal;
    });
  }

  public setReadyState(isReady: boolean): void {
    runInAction(() => {
      this._areImagesReady = isReady;
    });
  }

  public setPolylineInfo(points: Array<THREE.Vector3>, normals: Array<THREE.Vector3>): boolean {
    return this._polylineInfo.setInfo(points, normals);
  }

  public setImageMode(mode: ImagesMode): void {
    runInAction(() => {
      this._imageMode = mode;
    });
  }

  public setImageLoadingProgress(isReduced: boolean, framesLoaded: number, framesTotal: number): void {
    runInAction(() => {
      this._areReducedImagesLoading = isReduced;
      this._totalFramesCount = framesTotal;
      this._loadedFramesCount = framesLoaded;
    });
  }

  @computed
  public get slicesCount(): number {
    if (this._imageMode === ImagesMode.POLYLINE) {
      return this._polylineInfo.slicesCount;
    }
    return this._slicesCount;
  }

  @computed
  public get imageMode(): ImagesMode {
    return this._imageMode;
  }

  @computed
  public get polylineInfo(): IImagesPolylineInfo {
    return this._polylineInfo;
  }

  @computed
  public get currentSlice(): number {
    if (this._imageMode === ImagesMode.POLYLINE) {
      return this._polylineInfo.currentSlice;
    }
    return this._currentSlice;
  }

  @computed
  public get xRaySlicerParams(): XRaySlicerParams | undefined {
    return this._xRaySlicerParams;
  }

  @computed
  public get sliceDistance(): number {
    return this._sliceDistance;
  }

  @computed
  public get contrastWindowLevel(): number {
    return this._contrastWindowLevel;
  }

  @computed
  public get contrastWindowWidth(): number {
    return this._contrastWindowWidth;
  }

  @computed
  public get reductionFactorFromOriginal(): number {
    return this._reductionFactorFromOriginal;
  }

  @computed
  public get slicePosition(): number {
    return this._slicePosition;
  }

  @computed
  public get areImagesReady(): boolean {
    return this._areImagesReady;
  }

  @computed
  public get loadingPercentage(): number {
    if (this._totalFramesCount === 0) return 0;
    return Math.round((this._loadedFramesCount / this._totalFramesCount) * 100);
  }

  @computed
  public get shouldShowLoadingPercentage(): boolean {
    return this._totalFramesCount !== 0 && this.loadingPercentage !== 100;
  }

  @computed
  public get areReducedImagesLoading(): boolean {
    return this._areReducedImagesLoading;
  }

  @computed
  public get imageReductionFactor(): number {
    return this._imageReductionFactor;
  }
}
